import { useCallback, useEffect, useState } from 'react';
import * as db from '../lib/data.js';
import { MOCK } from '../lib/supabase.js';
import { buildPermitIndex, summarizePermits, flagChip, agencyWhen, agencyCardState, PERMIT_AGENCY, UNKNOWN_TEXT } from '../lib/permit-flags.js';

// กล่อง "เรื่องใบอนุญาต" + ป้ายรายบรรทัด — ใช้ทั้งหน้าตรวจไฟล์ invoice และหน้าใบข้อมูลสินค้า
// ⚠ กล่องนี้ noprint และอยู่นอก .a4 เสมอ: เอกสารส่ง broker/ไฟล์ Excel ห้ามมีคำเตือนของเรา (ช่อง License ของชิปปิ้ง)
// ⚠ ไม่มีสีเขียวและไม่มีคำว่า "ไม่ต้องขอ" — ไม่เจอในระบบ ≠ ไม่ต้องขอ (ดู permit-flags.js)

// หน่วยงานที่ใช้ช่อง import_license_other ฝั่งไทยช่องเดียวกัน
const SHARED = ['dnp', 'fisheries', 'other'];

// กฎพิกัด → ดัชนี · โหลดไม่ได้ = status 'failed' ขึ้นว่าตรวจไม่ได้ ไม่ใช่กล่องว่าง ๆ ที่อ่านเป็น "ไม่มีอะไรต้องขอ"
export function usePermitIndex() {
  const [state, setState] = useState({ status: 'loading', index: null });
  const load = useCallback(() => {
    if (MOCK) { setState({ status: 'failed', index: null }); return; }
    setState(s => ({ ...s, status: 'loading' }));
    db.loadPermitRules()
      .then(rules => {
        const index = buildPermitIndex(rules);
        setState(index.size ? { status: 'ok', index } : { status: 'failed', index: null });
      })
      .catch(() => setState({ status: 'failed', index: null }));
  }, []);
  useEffect(() => { load(); }, [load]);
  return { ...state, retry: load };
}

// ป้ายของหนึ่งแถว: กดแล้วบอกเหตุผล (มือถือไม่มี hover) · open/onToggle ถือค่าที่แถว
export function PermitChips({ result, open, onToggle, compact = false }) {
  if (!result) return null;
  const { flags, level, reason } = result;
  if (level === 'quiet') return null;
  const items = flags.map(f => ({ key: f.agency, cls: f.level === 'must' ? 'a' : 'b', text: flagChip(f), why: f.whys.join(' · ') }));
  // มีป้ายแต่ยังไม่รู้พิกัด: อย่าให้อ่านเหมือนตรวจครบแล้ว
  if (reason && !(level === 'must' && reason === 'unconfirmed')) items.push({ key: 'unknown', cls: 'c', text: 'ยังบอกไม่ได้', why: UNKNOWN_TEXT[reason] ?? '' });
  return (
    <>
      <span className={`ii-pm ${compact ? 'compact' : ''}`}>
        {items.map(i => (
          <button type="button" key={i.key} className={`pm-tag ${i.cls}`} aria-expanded={!!open} title={i.why}
            onClick={onToggle}>{i.text}</button>
        ))}
      </span>
      {open && <span className="ii-pmwhy">{items.map(i => <span key={i.key}>{i.why}</span>)}</span>}
    </>
  );
}

function LineList({ lines, onOpenPermit }) {
  return (
    <div className="pmw-list">
      {lines.map(li => (
        <div className="pmw-row" key={li.key}>
          <span className="pmw-sku">{li.sku || '—'}</span>
          <span className="pmw-nm">{li.name || ''}</span>
          {li.cardId && (onOpenPermit
            ? <button type="button" className="pm-link" onClick={() => onOpenPermit(li)}>เปิดเอกสารยื่น →</button>
            : <a className="pm-link" href={`/permit/${encodeURIComponent(li.cardId)}`} target="_blank" rel="noopener noreferrer"
                title="เปิดหน้าเอกสารยื่นขอใบอนุญาตของรุ่นนี้ในแท็บใหม่ — หน้านี้ยังอยู่เหมือนเดิม">เปิดเอกสารยื่น →</a>)}
        </div>
      ))}
    </div>
  );
}

// เปิด/ปิดรายการจำต่อใบในแท็บนี้ — กดไปหน้าเอกสารยื่นแล้วย้อนกลับ หน้านี้เริ่มใหม่ ถ้าไม่จำต้องกดเปิดซ้ำทุกรุ่น
function useRemembered(key, initial) {
  const [v, setV] = useState(() => {
    if (!key) return initial;
    try { const x = sessionStorage.getItem(key); return x == null ? initial : x === '1'; } catch { return initial; }
  });
  const toggle = () => setV(o => {
    try { if (key) sessionStorage.setItem(key, o ? '0' : '1'); } catch { /* ไม่จำก็ไม่เป็นไร */ }
    return !o;
  });
  return [v, toggle];
}

function Group({ g, agency, memoKey, onOpenPermit }) {
  const [show, toggleShow] = useRemembered(memoKey ? `ib_pm_group:${memoKey}:${agency}:${g.key}` : null, false);
  // เอกสารต้นทางไม่มีคำแนะนำกลาง ชื่อเอกสารอยู่ในหมายเหตุ จึงเปิดไว้เลย
  const [noteOpen, setNoteOpen] = useState(agency === 'origin');
  const label = g.level === 'must' ? 'ต้องขอ' : 'ฝากเช็ค';
  // คนต้องเห็นเลยว่ารายการไหน ไม่ต้องกดก่อน · ยาวเกินค่อยกดดูทั้งหมด
  const shown = g.lines.slice(0, 8);
  const more = g.lines.length - shown.length;
  return (
    <div className={`pmw-g ${g.level}`}>
      <div className="pmw-gh">
        <span className="pmw-gl">{label}{g.hs ? ` · พิกัด ${g.hs}` : ''} · {g.lines.length} รายการ</span>
        <button type="button" className="pm-toggle" aria-expanded={show} onClick={toggleShow}>{show ? '▴ ซ่อนรายการ' : '▾ ดูรายการ'}</button>
        {g.flag.note && <button type="button" className="pm-toggle" aria-expanded={noteOpen} onClick={() => setNoteOpen(v => !v)}>หมายเหตุของพิกัดนี้</button>}
      </div>
      <div className="pmw-skus">{shown.map(l => l.sku || l.name).join(' · ')}{more > 0 ? ` และอีก ${more} รายการ` : ''}</div>
      <div className="pmw-why">{g.level === 'must' && g.flag.source ? `ที่มา: ${g.flag.source}` : g.whys.join(' · ')}</div>
      {noteOpen && g.flag.note && <div className="pmw-note">{g.flag.note}</div>}
      {show && <LineList lines={g.lines} onOpenPermit={onOpenPermit}/>}
    </div>
  );
}

function SimpleFold({ text, lines, memoKey, onOpenPermit }) {
  const [show, toggleShow] = useRemembered(memoKey ? `ib_pm_unknown:${memoKey}` : null, false);
  return (
    <div className="pmw-foot">
      <span>{text}</span>
      {lines.length > 0 && <button type="button" className="pm-toggle" aria-expanded={show} onClick={toggleShow}>{show ? '▴ ซ่อนรายการ' : '▾ ดูรายการ'}</button>}
      {show && <LineList lines={lines} onOpenPermit={onOpenPermit}/>}
    </div>
  );
}

// lines = [{ key, sku, name, cardId, result, on? }] · on === false = ไม่ได้ติ๊กเอาเข้าใบ (หน้าตรวจไฟล์)
export default function PermitPanel({ mode = 'import', memoKey = null, closed = false, status, lines, reqs = null, onRetry, onOpenPermit, onMakeCards }) {
  const title = mode === 'import' ? 'เรื่องใบอนุญาตของไฟล์นี้' : 'เรื่องใบอนุญาตของใบนี้';
  // หน้าใบข้อมูลสินค้าเปิดซ้ำบ่อย: พับได้ และจำต่อใบในแท็บนี้ · หน้าตรวจไฟล์เปิดเสมอ
  const [open, setOpen] = useState(() => {
    if (!memoKey) return true;
    // ตู้ปิดแล้วไม่มีอะไรให้ทำต่อ เริ่มแบบย่อ
    try { const v = sessionStorage.getItem(`ib_pm_open:${memoKey}`); return v == null ? !closed : v !== '0'; } catch { return !closed; }
  });
  const toggleOpen = () => setOpen(v => {
    try { if (memoKey) sessionStorage.setItem(`ib_pm_open:${memoKey}`, v ? '0' : '1'); } catch { /* ไม่จำก็ไม่เป็นไร */ }
    return !v;
  });

  if (status === 'loading') return (
    <div className="pmw quiet noprint"><div className="pmw-t">{title}</div><div className="pmw-s">กำลังตรวจเรื่องใบอนุญาต…</div></div>
  );
  if (status !== 'ok') return (
    <div className="pmw quiet noprint">
      <div className="pmw-t">{title}</div>
      <div className="pmw-s">
        {mode === 'import'
          ? 'ตรวจเรื่องใบอนุญาตไม่สำเร็จ ใส่รายการเข้าใบนี้ต่อได้ แล้วระบบจะลองอีกครั้งที่หน้าใบข้อมูลสินค้า'
          : 'ตรวจเรื่องใบอนุญาตไม่สำเร็จ ยังบอกไม่ได้ว่ารายการไหนต้องขอ'}
      </div>
      {onRetry && <button type="button" className="btn2 sm" onClick={onRetry}>ลองอีกครั้ง</button>}
    </div>
  );

  const on = lines.filter(l => l.on !== false);
  const off = lines.length - on.length;
  const sum = summarizePermits(on);
  const { must, check, unknown, quiet, total } = sum.counts;
  const unknownLines0 = sum.unknownLines.length;
  const count = [must && `ต้องขอ ${must} รายการ`, sum.counts.checkLines && `ฝากเช็ค ${sum.counts.checkLines}`, unknownLines0 && `ยังบอกไม่ได้ ${unknownLines0}`].filter(Boolean).join(' · ');
  const u = sum.unknown;
  const unknownLines = sum.unknownLines;
  const unknownText = [u.nocard.length && `ยังไม่มีใบข้อมูลสินค้า ${u.nocard.length}`, u.nohs.length && `ยังไม่มีพิกัด ${u.nohs.length}`,
    u.norule.length && `พิกัดยังไม่มีในระบบ ${u.norule.length}`, u.badhs.length && `ช่องพิกัดอ่านไม่ออก ${u.badhs.length}`,
    u.unconfirmed.length && `พิกัดรอยืนยัน ${u.unconfirmed.length}`].filter(Boolean).join(' · ');

  if (!must && !check && !unknown) return (
    <div className="pmw quiet noprint">
      <div className="pmw-t">{title}</div>
      <div className="pmw-s">
        ยังไม่พบเรื่องใบอนุญาตจากพิกัดและรายการที่เคยต้องแจ้งนำเข้า {total ? `ของ ${total} รายการนี้ ` : ''}— แปลว่ายังไม่มีข้อมูล ไม่ได้ยืนยันว่าไม่ต้องขอ
        · ฝากช่วยถามชิปปิ้งก่อนของออกทุกรอบ
      </div>
      {off > 0 && <div className="pmw-foot"><span>ไม่นับ {off} รายการที่ไม่ได้ติ๊กไว้</span></div>}
    </div>
  );

  return (
    <div className={`pmw noprint ${must || check ? '' : 'quiet'}`}>
      <div className="pmw-th">
        <span className="pmw-t">{title}</span>
        {memoKey && <button type="button" className="pm-toggle" aria-expanded={open} onClick={toggleOpen}>{open ? '▴ ย่อ' : '▾ ดูรายละเอียด'}</button>}
      </div>
      {closed && <div className="pmw-s">ตู้นี้ปิดงานแล้ว ข้อมูลนี้ไว้ดูอ้างอิงเท่านั้น</div>}
      <div className="pmw-count">{count}{!open && sum.agencies.some(a => a.must) ? ` — ${sum.agencies.filter(a => a.must).map(a => `${a.meta.chip} ${a.must}`).join(' · ')}` : ''}</div>
      {open && <>
      <div className="pmw-s">เทียบจากกฎพิกัดและรายการที่เคยต้องแจ้งนำเข้า ช่วยเตือนเบื้องต้นเท่านั้น คำตอบจริงยึดตามหน่วยงานและชิปปิ้ง</div>
      {sum.agencies.map(a => {
        const shared = SHARED.includes(a.agency) && sum.agencies.filter(x => x.must > 0 && SHARED.includes(x.agency)).length > 1;
        const card = reqs && a.must > 0 ? agencyCardState(a.agency, reqs, { shared }) : null;
        const when = agencyWhen(a);
        return (
          <div className="pmw-a" key={a.agency}>
            <div className="pmw-ah">
              <span className={`pm-tag ${a.must ? 'a' : 'b'} static`}>{a.meta.chip}</span>
              <span className="pmw-an">{a.meta.name}</span>
              {card && <span className={`pmw-card ${card.key}`}>{card.text}</span>}
            </div>
            {!closed && when && a.must > 0 && <div className="pmw-s">{when}</div>}
            {!closed && PERMIT_AGENCY[a.agency].advice && (
              <div className="pmw-s">{PERMIT_AGENCY[a.agency].advice}
                {PERMIT_AGENCY[a.agency].link && a.must > 0 && <> · <a className="pm-link" href={PERMIT_AGENCY[a.agency].link.href} target="_blank" rel="noopener noreferrer">{PERMIT_AGENCY[a.agency].link.text}</a></>}
              </div>
            )}
            {a.groups.map(g => <Group key={g.key} g={g} agency={a.agency} memoKey={memoKey} onOpenPermit={onOpenPermit}/>)}
          </div>
        );
      })}
      {reqs && onMakeCards && sum.agencies.some(a => a.must > 0 && agencyCardState(a.agency, reqs).key === 'none') && (
        <div className="pmw-foot"><button type="button" className="btn2 sm" onClick={onMakeCards}>สร้างช่องแนบเอกสารในตู้</button></div>
      )}
      {unknownLines.length > 0 && (
        <SimpleFold text={`ยังบอกไม่ได้ ${unknownLines.length} รายการ: ${unknownText}`} lines={unknownLines} memoKey={memoKey} onOpenPermit={onOpenPermit}/>
      )}
      {quiet > 0 && <div className="pmw-foot"><span>อีก {quiet} รายการ ระบบไม่พบข้อกำหนด — แปลว่ายังไม่มีข้อมูล ไม่ได้ยืนยันว่าไม่ต้องขอ</span></div>}
      {off > 0 && <div className="pmw-foot"><span>ไม่นับ {off} รายการที่ไม่ได้ติ๊กไว้</span></div>}
      </>}
    </div>
  );
}
